import React from 'react';
import { NavLink } from 'react-router-dom';
import SEO from '../components/SEO';
import { SERVICES as STATIC_SERVICES, COMPANY_INFO as STATIC_COMPANY_INFO } from '../data/siteData';
import { useServices, useCompanyInfo, usePageData } from '../lib/cms';

export default function ServicesPage() {
  const { services: SERVICES } = useServices();
  const { companyInfo: COMPANY_INFO } = useCompanyInfo();
  const { page } = usePageData('ydelser');
  const heroBg = page.heroImage || '';

  return (
    <>
      <SEO
        title="Ydelser - CMSNord.dk"
        description="Oversigt over ydelser hos CMSNord.dk i Brønderslev: Entreprenørarbejde, vognmandskørsel, anlægsgartner og belægning."
        canonicalUrl={`${window.location.origin}/ydelser`}
        ogImage={heroBg}
      />

      {/* Header Banner with high quality background image & dark overlay */}
      <section className="relative bg-[#121212] text-white py-16 sm:py-24 border-b border-zinc-800 overflow-hidden">
        {heroBg && (
          <div className="absolute inset-0 z-0">
            <img
              src={heroBg}
              alt="CMSNord.dk Ydelser"
              className="w-full h-full object-cover opacity-35 mix-blend-luminosity"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#121212]/95 via-[#121212]/80 to-[#121212]/60" />
          </div>
        )}

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <span className="text-[#F4AF00] text-xs font-bold uppercase tracking-widest drop-shadow">
            Professionelle Løsninger
          </span>
          <h1 className="text-3xl sm:text-5xl font-black text-white mt-1 mb-4 drop-shadow">
            Vores Ydelser
          </h1>
          <p className="text-zinc-200 text-base max-w-2xl drop-shadow">
            CMSNord.dk tilbyder et bredt spektrum af entreprenør- og anlægsydelser til erhverv, landbrug og private i Brønderslev og hele Nordjylland.
          </p>
        </div>
      </section>

      {/* Services Grid (2 Columns on desktop, image above text, no icons) */}
      <section className="py-16 bg-[#F4F4F6] text-[#121212] border-b border-zinc-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {SERVICES.map((s) => (
              <div
                key={s.slug}
                className="bg-white border border-zinc-300 rounded-none overflow-hidden shadow-md hover:shadow-xl transition-shadow flex flex-col justify-between border-t-4 border-t-[#F4AF00]"
              >
                {/* Image on Top */}
                {s.primaryImage && (
                  <div className="relative h-60 sm:h-64 w-full overflow-hidden bg-[#121212] rounded-none">
                    <img
                      src={s.primaryImage}
                      alt={s.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500 rounded-none"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#121212]/60 to-transparent" />
                  </div>
                )}

                {/* Content Below */}
                <div className="p-6 sm:p-8 flex-1 flex flex-col justify-between space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="w-3.5 h-3.5 rounded-full bg-[#F4AF00] shrink-0" />
                      <h2 className="text-2xl font-black text-[#121212] uppercase tracking-tight">{s.title}</h2>
                    </div>
                    <p className="text-zinc-600 text-sm leading-relaxed">
                      {s.shortDesc}
                    </p>
                  </div>

                  <div className="pt-4 border-t border-zinc-200 flex flex-wrap gap-3 items-center">
                    <NavLink
                      to={`/ydelser/${s.slug}`}
                      className="px-5 py-2.5 bg-[#F4AF00] hover:bg-[#D99B00] text-[#121212] font-black text-xs uppercase tracking-wider transition-all shadow-sm flex items-center gap-2"
                    >
                      <span>Læs mere</span>
                      <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                    </NavLink>

                    <a
                      href={`tel:${COMPANY_INFO.phoneRaw}`}
                      className="nowrap-contact px-4 py-2.5 bg-zinc-100 hover:bg-zinc-200 border border-zinc-300 text-[#121212] font-bold text-xs transition-colors flex items-center gap-2"
                    >
                      <i className="fa-solid fa-phone text-[#F4AF00]" aria-hidden="true" />
                      <span>Ring: {COMPANY_INFO.phone}</span>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
